import React, { useEffect, useId, useMemo, useRef, useState } from 'react'
import { Popper } from '@mui/material'
import { ChevronDown, Search } from 'lucide-react'
import Input from '../../ui/Input'
import Select from '../../ui/Select'
import type { AppendixCodeItemOption } from '../../../types/codeIndex'
import {
  AppendixColumnDraft,
  AppendixRefIndex,
  MISMATCH_VALUE_LABEL,
  MISSING_VALUE_LABEL,
  columnHoldsRefs,
  getCellRefState,
} from '../../../utils/appendixTable'

type AppendixCellInputProps = {
  column: AppendixColumnDraft
  // The saved code item (or composite position range) a code item column points at, with the values
  // that fit it. Null when it has none or it no longer exists.
  codeItem: AppendixCodeItemOption | null
  refIndex: AppendixRefIndex
  // Text, or for a code item column the id of the chosen value.
  value: string
  invalid?: boolean
  disabled?: boolean
  ariaLabel: string
  onChange: (value: string, validateNow?: boolean) => void
  onBlur: () => void
  onClear?: () => void
}

const INVALID_CELL_CLASS = '!bg-[#FFF5F5]'
const CELL_ACTION_CLASS = 'cursor-pointer text-[11px] font-medium hover:underline'

const formatValueLabel = (option: { code: string; description: string }) => {
  const description = option.description.trim()
  return description && description !== option.code ? `${option.code} — ${description}` : option.code
}

const popperModifiers = [
  { name: 'offset', options: { offset: [0, 4] } },
  { name: 'preventOverflow', options: { padding: 12 } },
  { name: 'flip', options: { padding: 12 } },
]

// Picks a value of the column's code item. The cell stores the value's id and always shows what
// that value currently says; a value that no longer exists never shows the text it was picked with.
const CodeItemValueSelector: React.FC<AppendixCellInputProps> = ({
  column,
  codeItem,
  refIndex,
  value,
  invalid = false,
  disabled = false,
  ariaLabel,
  onChange,
  onBlur,
  onClear,
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const listboxId = useId()
  const containerRef = useRef<HTMLDivElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)
  const holdsRefs = columnHoldsRefs(column)
  const state = getCellRefState(column, value, refIndex)
  // A composite code item without a position range offers no values until one is picked.
  const selectable = Boolean(codeItem) && !codeItem?.positions?.length
  const values = useMemo(() => (selectable && codeItem ? codeItem.values : []), [codeItem, selectable])
  // The chosen value as the list shows it; text waiting to be matched shows as is.
  const selectedLabel = !holdsRefs
    ? value
    : state.status === 'Missing' || state.status === 'Empty'
      ? ''
      : formatValueLabel({ code: state.optionCode ?? '', description: state.description ?? '' })

  const filteredValues = useMemo(() => {
    const query = searchQuery.trim().toLowerCase()
    if (!query) return values
    return values.filter(option =>
      option.description.toLowerCase().includes(query) || option.code.toLowerCase().includes(query)
    )
  }, [searchQuery, values])

  useEffect(() => {
    if (!isOpen) return undefined

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node
      if (containerRef.current?.contains(target) || menuRef.current?.contains(target)) return
      setIsOpen(false)
      setSearchQuery('')
      onBlur()
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [isOpen, onBlur])

  const select = (key: string) => {
    onChange(key, true)
    setIsOpen(false)
    setSearchQuery('')
  }

  const isMissing = state.status === 'Missing'
  const isMismatch = state.status === 'Mismatch'
  const canPick = !disabled && selectable
  const flagged = invalid || isMissing || isMismatch

  const renderLabel = () => {
    // Until the column has a code item the cell holds text waiting to be matched.
    if (!holdsRefs) return <span className={`truncate ${value ? 'text-gray-900' : 'text-gray-400'}`}>{value || 'Select'}</span>
    if (isMissing) return <span className="truncate font-medium text-[#B3261E]">{MISSING_VALUE_LABEL}</span>
    if (state.status === 'Empty') return <span className="truncate text-gray-400">Select</span>
    return <span className="truncate text-gray-900">{selectedLabel}</span>
  }

  return (
    <div ref={containerRef} className="relative min-w-0">
      <button
        type="button"
        role="combobox"
        aria-label={ariaLabel}
        aria-haspopup="listbox"
        aria-controls={listboxId}
        aria-expanded={isOpen}
        aria-invalid={flagged || undefined}
        title={selectedLabel || undefined}
        disabled={!canPick}
        onClick={() => setIsOpen(current => !current)}
        className={`flex w-full items-center justify-between gap-1 rounded border px-2 py-1.5 text-left text-sm outline-none transition-colors disabled:cursor-not-allowed disabled:bg-[#F8FAFC] ${flagged ? `border-[#B3261E] ${INVALID_CELL_CLASS}` : isOpen ? 'border-[#1D74D8] bg-white ring-1 ring-[#1D74D8]' : 'border-[#CBD5E1] bg-white hover:border-[#1D74D8]'}`}
      >
        {renderLabel()}
        <ChevronDown className={`h-3.5 w-3.5 flex-shrink-0 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} aria-hidden="true" />
      </button>

      {isMismatch ? (
        <p className="mt-0.5 text-[11px] text-[#9A5B00]">{MISMATCH_VALUE_LABEL}</p>
      ) : null}
      {isMissing && !disabled ? (
        <div className="mt-0.5 flex flex-wrap gap-x-2">
          <button type="button" className={`${CELL_ACTION_CLASS} text-[#1D74D8]`} onClick={() => setIsOpen(true)} disabled={!canPick}>
            Choose another value
          </button>
          <button type="button" className={`${CELL_ACTION_CLASS} text-[#B3261E]`} onClick={onClear}>
            Clear
          </button>
        </div>
      ) : null}

      <Popper
        open={isOpen}
        anchorEl={containerRef.current}
        placement="bottom-start"
        modifiers={popperModifiers}
        sx={{ zIndex: 1600, minWidth: Math.max(containerRef.current?.offsetWidth || 0, 220) }}
      >
        <div ref={menuRef} className="overflow-hidden rounded-md border border-gray-200 bg-white shadow-lg">
          {values.length > 6 ? (
            <div className="border-b border-gray-100 p-2">
              <div className="relative">
                <Search className="absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" aria-hidden="true" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(event) => setSearchQuery(event.target.value)}
                  placeholder="Search"
                  aria-label="Search values"
                  className="w-full rounded border border-gray-200 py-1.5 pl-8 pr-2 text-sm focus:border-[#1D74D8] focus:outline-none"
                  autoFocus
                />
              </div>
            </div>
          ) : null}
          <div id={listboxId} role="listbox" aria-label={`${ariaLabel} values`} className="max-h-56 overflow-y-auto py-1">
            {value && !searchQuery.trim() ? (
              <button
                type="button"
                role="option"
                aria-selected={false}
                onClick={() => select('')}
                className="flex w-full items-center px-3 py-2 text-left text-sm italic text-gray-500 hover:bg-blue-50"
              >
                No value
              </button>
            ) : null}
            {filteredValues.length === 0 ? (
              <div className="px-3 py-2 text-sm text-gray-500">No values</div>
            ) : (
              filteredValues.map(option => (
                <button
                  key={option.key}
                  type="button"
                  role="option"
                  aria-selected={option.key === value}
                  title={formatValueLabel(option)}
                  onClick={() => select(option.key)}
                  className={`flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-blue-50 ${option.key === value ? 'bg-blue-50 text-[#1D74D8]' : 'text-gray-700'}`}
                >
                  <span className="min-w-0 flex-1 truncate">{formatValueLabel(option)}</span>
                </button>
              ))
            )}
          </div>
        </div>
      </Popper>
    </div>
  )
}

export const AppendixCellInput: React.FC<AppendixCellInputProps> = (props) => {
  const { column, value, invalid = false, disabled = false, ariaLabel, onChange, onBlur } = props

  if (column.dataType === 'CodeItem') {
    return <CodeItemValueSelector {...props} />
  }

  if (column.dataType === 'Boolean') {
    return (
      <Select
        value={value}
        invalid={invalid}
        disabled={disabled}
        aria-label={ariaLabel}
        onChange={(event) => onChange(event.target.value, true)}
        onBlur={onBlur}
        className={invalid ? INVALID_CELL_CLASS : ''}
      >
        <option value="">Select</option>
        <option value="true">Yes</option>
        <option value="false">No</option>
      </Select>
    )
  }

  return (
    <Input
      value={value}
      invalid={invalid}
      disabled={disabled}
      aria-label={ariaLabel}
      inputMode={column.dataType === 'Number' ? 'decimal' : undefined}
      onChange={(event) => onChange(event.target.value)}
      onBlur={onBlur}
      className={invalid ? INVALID_CELL_CLASS : ''}
    />
  )
}

export default AppendixCellInput
